import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Autocomplete,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface ProcedureTemplate {
  name: string;
  specifics: string;
}

interface CatalogItem {
  name: string;
  basePrice: number;
}

interface ProcedureData {
  procedure: string;
  template: string;
  specifics: string;
  date: Date;
  performedBy: string;
}

interface AutocompleteOption {
  name: string;
  price?: number;
  group: 'priced' | 'terminology';
}

interface ProcedureDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ProcedureData) => Promise<void>;
  currentUserName: string;
  existingProcedure?: any;
  showAll?: boolean;
  catalogItems?: CatalogItem[];
}

const ProcedureDialog: React.FC<ProcedureDialogProps> = ({
  open, onClose, onSave, currentUserName, existingProcedure, showAll = true, catalogItems = [],
}) => {
  const [templates, setTemplates] = useState<ProcedureTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [date, setDate] = useState<Date>(new Date());
  const [procedure, setProcedure] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [specifics, setSpecifics] = useState<string>('');
  const [performedBy, setPerformedBy] = useState<string>(currentUserName);

  useEffect(() => {
    if (existingProcedure && open) {
      setDate(new Date(existingProcedure.date));
      setProcedure(existingProcedure.name || '');
      setSelectedTemplate(existingProcedure.category || '');
      setSpecifics(existingProcedure.description || existingProcedure.notes || '');
      setPerformedBy(existingProcedure.performedBy || currentUserName);
    } else if (!existingProcedure && open) {
      setDate(new Date());
      setProcedure('');
      setSelectedTemplate('');
      setSpecifics('');
      setPerformedBy(currentUserName);
    }
  }, [existingProcedure, open, currentUserName]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);

    fetch('/terminology/procedureTemplates.json')
      .then(res => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return res.json();
      })
      .then((data: ProcedureTemplate[]) => {
        setTemplates(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load procedure templates:', err);
        setError('Failed to load procedure templates');
        setLoading(false);
      });
  }, [open]);

  useEffect(() => {
    if (selectedTemplate && !existingProcedure) {
      const template = templates.find(t => t.name === selectedTemplate);
      if (template) {
        setSpecifics(template.specifics);
        if (!procedure) {
          setProcedure(template.name);
        }
      }
    }
  }, [selectedTemplate, templates, existingProcedure]);

  const autocompleteOptions = useMemo((): AutocompleteOption[] => {
    const pricedOptions: AutocompleteOption[] = catalogItems
      .filter(item => item.name)
      .map(item => ({
        name: item.name,
        price: item.basePrice,
        group: 'priced' as const,
      }));

    if (!showAll) return pricedOptions;

    const pricedNamesLower = new Set(pricedOptions.map(o => o.name.toLowerCase()));

    const templateNames = templates.map(t => t.name).sort();

    const terminologyOptions: AutocompleteOption[] = templateNames
      .filter(name => !pricedNamesLower.has(name.toLowerCase()))
      .map(name => ({
        name,
        group: 'terminology' as const,
      }));

    return [...pricedOptions, ...terminologyOptions];
  }, [catalogItems, templates, showAll]);

  const matchingTemplateNames = useMemo(() => {
    const trimmed = procedure.trim().toLowerCase();
    if (!trimmed) return [];
    return templates
      .filter(t => t.name.toLowerCase() === trimmed)
      .map(t => t.name);
  }, [procedure, templates]);

  useEffect(() => {
    if (matchingTemplateNames.length === 0 && selectedTemplate) {
      setSelectedTemplate('');
    }
  }, [matchingTemplateNames, selectedTemplate]);

  const handleSave = async () => {
    if (!procedure.trim()) {
      setError('Please enter a procedure name');
      return;
    }

    if (!specifics.trim()) {
      setError('Please enter procedure specifics');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onSave({
        procedure: procedure.trim(),
        template: selectedTemplate,
        specifics: specifics.trim(),
        date,
        performedBy: performedBy.trim(),
      });
      handleClose();
    } catch (err: any) {
      console.error('Failed to save procedure:', err);
      setError(err?.response?.data?.message || 'Failed to save procedure');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setDate(new Date());
      setProcedure('');
      setSelectedTemplate('');
      setSpecifics('');
      setPerformedBy(currentUserName);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{existingProcedure ? 'Edit Procedure' : 'New Procedure'}</DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label="Date & Time"
                    value={date}
                    onChange={(newDate) => setDate(newDate || new Date())}
                    slotProps={{ textField: { fullWidth: true, required: true } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Performed By"
                    fullWidth
                    value={performedBy}
                    onChange={(e) => setPerformedBy(e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    options={autocompleteOptions}
                    value={autocompleteOptions.find(o => o.name === procedure) || null}
                    onChange={(_, newValue) => {
                      const name = typeof newValue === 'string' ? newValue : newValue?.name || '';
                      setProcedure(name);
                    }}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      return option.price != null
                        ? `${option.name} — $${option.price.toFixed(2)}`
                        : option.name;
                    }}
                    groupBy={showAll ? (option) => option.group === 'priced' ? 'Your Priced Items' : 'Medical Database' : undefined}
                    renderOption={(props, option) => (
                      <li {...props} key={`${option.name}-${option.group}`}>
                        <Box display="flex" flexDirection="column">
                          <Typography variant="body2" fontWeight={600}>
                            {option.name}
                            {option.price != null && (
                              <Typography component="span" variant="body2" color="success.main" sx={{ ml: 1 }}>
                                ${option.price.toFixed(2)}
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={params => (
                      <TextField {...params} label="Procedure" required />
                    )}
                    filterOptions={(options, { inputValue }) => {
                      const lower = inputValue.toLowerCase();
                      return options.filter(o => o.name.toLowerCase().includes(lower));
                    }}
                    isOptionEqualToValue={(option, value) => option.name === value.name}
                    freeSolo
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input') {
                        setProcedure(value);
                      }
                    }}
                    inputValue={procedure}
                  />
                </Grid>

                {matchingTemplateNames.length > 0 && (
                  <Grid item xs={12}>
                    <Autocomplete
                      options={matchingTemplateNames}
                      value={selectedTemplate}
                      onChange={(_, newValue) => setSelectedTemplate(newValue || '')}
                      renderInput={params => (
                        <TextField {...params} label="Template" placeholder="Select a template (optional)" />
                      )}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Select a template to auto-fill procedure details
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    label="Specifics"
                    fullWidth
                    multiline
                    rows={12}
                    value={specifics}
                    onChange={e => setSpecifics(e.target.value)}
                    placeholder="Enter procedure details, findings, and outcome..."
                    required
                  />
                </Grid>
              </Grid>
            </Box>
          </LocalizationProvider>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={submitting || loading}>
          {submitting ? 'Saving...' : (existingProcedure ? 'Update' : 'Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProcedureDialog;
